/**
 * ExportLensAnalysisReport - Export button component for lens analysis
 * ============================================================================
 * DC-1046: Export lens analysis as PDF or JSON
 *
 * Features:
 * - Dropdown button with PDF/JSON export options
 * - Downloads export file from server
 * - Loading state during export
 *
 * Pattern: Follows ExportCredibilityReport.tsx
 *
 * @version 1.0.0
 * @created 2026-01-25
 * ============================================================================
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import type { RegulationSchemaId } from './types';
import './ExportLensAnalysisReport.css';

// ============================================================================
// Types
// ============================================================================

export interface ExportLensAnalysisReportProps {
  /** Document ID to export */
  documentId: string;
  /** Regulation ID (for deep analysis export) */
  regulationId: RegulationSchemaId | null;
  /** Whether export is disabled */
  disabled?: boolean;
  /** Custom className */
  className?: string;
}

type ExportFormat = 'pdf' | 'json';

// ============================================================================
// Component
// ============================================================================

export const ExportLensAnalysisReport: React.FC<ExportLensAnalysisReportProps> = ({
  documentId,
  regulationId,
  disabled = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle export
  const handleExport = useCallback(
    async (format: ExportFormat) => {
      if (!regulationId) {
        setError('Please run deep analysis before exporting');
        return;
      }

      try {
        setExporting(true);
        setError(null);
        setIsOpen(false);

        const url = `/api/v1/governance-documents/${documentId}/export/${regulationId}?format=${format}`;
        const response = await fetch(url);

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Export failed');
        }

        // Get the blob and trigger download
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `lens-analysis-${documentId}-${regulationId}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      } catch (err) {
        console.error('[ExportLensAnalysisReport] Export error:', err);
        setError(err instanceof Error ? err.message : 'Export failed');
      } finally {
        setExporting(false);
      }
    },
    [documentId, regulationId]
  );

  const isDisabled = disabled || !regulationId || exporting;

  return (
    <div className={`elar-container ${className}`} ref={dropdownRef}>
      <button
        className="elar-button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isDisabled}
        title={!regulationId ? 'Run deep analysis to enable export' : 'Export analysis report'}
      >
        {exporting ? (
          <>
            <span className="elar-spinner" />
            Exporting...
          </>
        ) : (
          <>
            <svg
              className="elar-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 10v6m0 0l-3-3m3 3l3-3M3 17v3a2 2 0 002 2h14a2 2 0 002-2v-3"
              />
            </svg>
            Export
            <svg
              className={`elar-chevron ${isOpen ? 'elar-chevron--open' : ''}`}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </>
        )}
      </button>

      {isOpen && (
        <div className="elar-dropdown">
          <button
            className="elar-option"
            onClick={() => handleExport('pdf')}
            disabled={exporting}
          >
            <svg className="elar-option-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 2l5 5h-5V4zm-5 9h8v2H8v-2zm0 4h8v2H8v-2zm0-8h3v2H8V9z" />
            </svg>
            <div className="elar-option-content">
              <span className="elar-option-title">Export as PDF</span>
              <span className="elar-option-description">
                Professional report for compliance audits
              </span>
            </div>
          </button>
          <button
            className="elar-option"
            onClick={() => handleExport('json')}
            disabled={exporting}
          >
            <svg className="elar-option-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 2l5 5h-5V4zm-4.5 7.5a.5.5 0 01.5.5v1.5h1V12a.5.5 0 01.5-.5h1.5v-1H11a.5.5 0 01-.5-.5V8.5h-1V10a.5.5 0 01-.5.5H7.5v1H9z" />
            </svg>
            <div className="elar-option-content">
              <span className="elar-option-title">Export as JSON</span>
              <span className="elar-option-description">
                Machine-readable format for integration
              </span>
            </div>
          </button>
        </div>
      )}

      {error && (
        <div className="elar-error">
          <span className="elar-error-icon">!</span>
          <span className="elar-error-message">{error}</span>
          <button className="elar-error-dismiss" onClick={() => setError(null)}>
            X
          </button>
        </div>
      )}
    </div>
  );
};

export default ExportLensAnalysisReport;
